import React from 'react'
import { AsyncCreatable, Async } from 'react-select'

import enhance from '../InputAutoComplete/enhance'
import { Wrapper, Icon, Label } from '../InputAutoComplete/styles'
import '../InputAutoComplete/react-select-customized.css'
import debouncePromise from 'debounce-promise'
import { useCallback, useEffect, useRef } from 'react'

// react-select treats any non-null value as "has value" and hides the
// placeholder. Redux-form often seeds fields with empty objects like
// { name: '', npi: '' }, which makes the field look blank but still suppresses
// the placeholder. Normalize those to null so the placeholder is visible until
// a real selection is made.
const normalizeValue = v => {
  if (v == null) return null
  if (typeof v !== 'object') return v === '' ? null : v
  if (Array.isArray(v)) return v.length ? v : null
  const entries = Object.values(v)
  if (entries.length === 0) return null
  const hasRealContent = entries.some(val => {
    if (val == null || val === '') return false
    if (typeof val === 'object') return normalizeValue(val) != null
    return true
  })
  return hasRealContent ? v : null
}

const InputAutoCompleteStable = ({
  input: { value },
  meta: { visited },
  showLabel,
  label,
  beforeIcon,
  handleChange,
  handleFocus,
  handleBlur,
  selectValue,
  innerRef,
  selectedValue,
  handleKeyDown,
  isCreatable,
  dropDown = true,
  loadOptions,
  noResultsText,
  searchPromptText,
  className,
  ...rest
}) => {
  const wrapperRef = useRef(null)

  const debouncedLoadOptions = useCallback(debouncePromise(loadOptions, 300), [
    loadOptions,
  ])

  const wrapperClassName = [className, beforeIcon && 'hasIcon']
    .filter(Boolean)
    .join(' ')

  const normalizedValue = normalizeValue(selectedValue)

  useEffect(() => {
    const handleMouseDown = e => {
      const wrapper = wrapperRef.current
      if (!wrapper) return
      const selectEl = wrapper.querySelector('.Select')
      if (!selectEl || !selectEl.classList.contains('has-value')) return
      if (selectEl.classList.contains('is-open')) return
      const controlEl = selectEl.querySelector('.Select-control')
      if (!controlEl || !controlEl.contains(e.target)) return
      const clearZone = selectEl.querySelector('.Select-clear-zone')
      if (clearZone && clearZone.contains(e.target)) return
      // Find the title element inside the value renderer. The expected structure is
      //   .Select-value-label > [span] > div (body) > div:first-child (title)
      // Some call sites wrap ComponentValue in an extra <span data-testid=...>,
      // so match either depth with two selectors.
      const valueLabelEl =
        selectEl.querySelector('.Select-value-label > div > div:first-child') ||
        selectEl.querySelector(
          '.Select-value-label > span > div > div:first-child',
        )
      if (!valueLabelEl || !valueLabelEl.textContent.trim()) return
      e.stopImmediatePropagation()
      e.preventDefault()
      const range = document.createRange()
      range.selectNodeContents(valueLabelEl)
      const sel = window.getSelection()
      sel.removeAllRanges()
      sel.addRange(range)
    }

    document.addEventListener('mousedown', handleMouseDown, true)
    return () =>
      document.removeEventListener('mousedown', handleMouseDown, true)
  }, [])

  return (
    <Wrapper ref={wrapperRef} className={wrapperClassName} dropDown={dropDown}>
      {beforeIcon && <Icon name={beforeIcon} />}
      {label && (
        <Label
          visited={showLabel || visited || normalizedValue}
          className="label"
        >
          {label}
        </Label>
      )}
      {React.createElement(isCreatable ? AsyncCreatable : Async, {
        ref: innerRef,
        className: (beforeIcon && 'hasIcon') || '',
        arrowRenderer: null,
        onChange: handleChange,
        value: normalizedValue,
        onFocus: handleFocus,
        onBlur: handleBlur,
        onInputKeyDown: handleKeyDown,
        inputProps: {
          'data-testid': rest['data-testid'],
        },
        loadOptions: debouncedLoadOptions,
        noResultsText: noResultsText || 'Type to search',
        searchPromptText: searchPromptText || 'Type to search',
        ...rest,
      })}
    </Wrapper>
  )
}

export default enhance(InputAutoCompleteStable)
